<!-- Note for DEV:
Make the tab content visible when the user tabs to an item then presses enter on the keyboard. -->
<template>
	<div :class="['pill-tabs', ...variantClassList]">
		<div class="pill-tabs__header-container">
			<div class="pill-tabs__header">
				<ul :class="['pill-tabs__header-items']">
					<li
						:class="[
							'pill-tabs__header-item',
							currentTabIndex === tab.index && 'is-active',
							tab.disabled && 'no-interactive',
							tab.hasError && 'has-error'
						]"
						v-for="tab in tabs"
						:key="tab.index"
						@click="!tab.disabled ? onTabClick(tab.index) : null"
						tabindex="0"
					>
						<div
							:class="[
								'pill-tabs__header-element',
								tab.disabled && 'is-disabled'
							]"
						>
							<div
								class="pill-tabs__header-element-content"
								v-if="
									$slots['headerItem' + tab.index] ===
										undefined
								"
							>
								<span class="pill-tabs__title" v-if="tab.title">
									{{ tab.title }}
									<TabIconBadge
										:indicator="(tab.hasError ? 'error' : tab.indicator)"
										:tabIcon="tab.tabIcon"
										:dataBg="tab.dataBg"
										:dataColor="tab.dataColor"
										:indicatorValue="tab.indicatorValue"
										v-if="
											(tab.indicator ||
												tab.tabIcon ||
												tab.indicatorValue ||
												tab.hasError) &&
												$slots[
													'headerItem' + tab.index
												] === undefined
										"
									/>
								</span>
							</div>
							<slot :name="'headerItem' + tab.index"></slot>
						</div>
					</li>
				</ul>
			</div>
		</div>
		<div class="pill-tabs__tab-content">
			<slot :name="currentTabIndex" />
		</div>
	</div>
</template>

<script>
import tabsMixin from './tabs-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/pill-tabs-variant-classes';

export default {
	name: 'PillTabs',
	mixins: [tabsMixin, componentVariantMixin],
	props: {
		hasError: {
			type: Boolean,
			default: false
		}
	},
	created() {
		this.variantClasses = variantClasses;
	},
};
</script>

<style lang="scss" scoped>
.pill-tabs {
	$tab-nav-height: 40px;
	$tab-nav-offset: calc($tab-nav-height / 2);

	&__header-container {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__header {
		-webkit-align-items: center;
		-webkit-box-align: center;
		-ms-flex-align: center;
		align-items: center;
		display: inline-block;
		position: relative;
		white-space: nowrap;
		position: relative;
		background: var(--bg-0);
		border-radius: 3em;

		&::before {
			position: absolute;
			content: '';
			@include transform-full-center;
			width: 100%;
			height: 100%;
			border-radius: 3em;
			border: 2px solid var(--neutral-2);
		}
	}

	&__title {
		display: inline-flex;
		align-items: center;
		gap: $gutter-lg;
		height: $tab-nav-height;
	}

	&__header-items {
		display: inline-flex;
		align-items: stretch;
	}

	&__header-item {
		@include heading('sm');
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		padding: 0 $padding-xxl;
		-webkit-transition: all 100ms ease-out;
		transition: all 100ms ease-out;
		-webkit-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		user-select: none;
		border-radius: 3em;
		color: var(--neutral-4);
		background: transparent;
		position: relative;

		&:focus,
		&:focus-visible,
		&:visited {
			outline: none;
		}

		&:hover,
		&:focus {
			&::after {
				position: absolute;
				content: '';
				@include transform-full-center;
				width: calc(100%);
				height: calc(100%);
				border-radius: 3em;
				border: 3px solid var(--brand-2);
			}
		}
	}

	&__header-element {
		display: inline-block;
		position: relative;
	}

	&__tab-content {
		margin-top: -$tab-nav-offset; //  this value is dependent on the height of the Pill Tab; it should be half the height
		padding: ($padding-md + $tab-nav-offset)  $padding-md $padding-md $padding-md;
		background-color: var(--bg-0);
		color: var(--bg-0-fg-4);
	}

	.is-active {
		background: var(--brand-2-tint);
		color: var(--neutral-5);
		border-color: var(--neutral-2);
		&:deep(.color-indicator__square) {
			background: var(--ui-accessible-need);
		}

		&::after {
			border-color: transparent;
		}
	}
	.no-interactive {
		&:hover,
		&:focus {
			&::after {
				border-color: transparent;
				cursor: default;
			}
		}
	}

	.has-indicator-error {
		&::after {
			border-color: var(--ui-negative);
		}
	}

	& .has-error.is-active {
		background: var(--ui-negative-light);
		color: $ui-fg-dark;

		&::after {
			position: absolute;
			content: '';
			@include transform-full-center;
			width: calc(100%);
			height: calc(100%);
			border-radius: 3em;
			border: 3px solid var(--ui-negative);
		}

		&:hover,
		&:focus {
			&::after {
				border-color: var(--ui-negative);
			}
		}
	}
}

/* Variant Styles */

.pill-tabs--bg-1 {
	.pill-tabs__tab-content {
		background-color: var(--bg-1);
		color: var(--bg-1-fg-3);
	}
}

.pill-tabs--bg-2 {
	.pill-tabs__tab-content {
		background-color: var(--bg-2);
		color: var(--bg-2-fg-3);
	}
}

.pill-tabs--condensed--bg-0,
.pill-tabs--condensed--bg-1,
.pill-tabs--condensed--bg-2 {
	$tab-nav-height: 24px;
	$tab-nav-offset: calc($tab-nav-height / 2);

	&:deep(.color-indicator:first-child) {
		transform: scale(0.8);
	}

	.pill-tabs__title {
		height: $tab-nav-height;
		gap: $gutter-md;
	}

	.pill-tabs__header-item {
		padding: 0 $padding-lg;
	}

	.pill-tabs__tab-content {
		margin-top: -$tab-nav-offset; //  this value is dependent on the height of the Pill Tab; it should be half the height
		padding-top: $padding-md + $tab-nav-offset;
	}
}

.pill-tabs--condensed--bg-0 {
	.pill-tabs__tab-content {
		background-color: var(--bg-0);
		color: var(--bg-0-fg-4);
	}
}

.pill-tabs--condensed--bg-1 {
	.pill-tabs__tab-content {
		background-color: var(--bg-1);
		color: var(--bg-1-fg-3);
	}
}

.pill-tabs--condensed--bg-2 {
	.pill-tabs__tab-content {
		background-color: var(--bg-2);
		color: var(--bg-2-fg-3);
	}
}
</style>
